interface TimeFormatOptions {
  showDate?: boolean;
  showTime?: boolean;
  showDay?: boolean;
  showYear?: boolean;
  showSeconds?: boolean;
  use24Hour?: boolean;
  useFullMonth?: boolean;
  useFullDay?: boolean;
  useFullDateTime?: boolean;
  useNumericTimeFormat?: boolean;
  locale?: string;
  timeZone?: string;
}

interface DayFormatOptions {
  showDay?: boolean;
  showYear?: boolean;
  useFullMonth?: boolean;
  useFullDay?: boolean;
  noDayString?: boolean;
  useShortMonth?: boolean;
  useShortDay?: boolean;
  useNumericFormat?: boolean;
  locale?: string;
  timeZone?: string;
}

const defaultOptions: Required<TimeFormatOptions> = {
  showDate: true,
  showTime: true,
  showDay: false,
  showYear: true,
  showSeconds: false,
  use24Hour: false,
  useFullMonth: false,
  useFullDay: false,
  useFullDateTime: false,
  useNumericTimeFormat: false,
  locale: 'de-DE',
  timeZone: 'Europe/Berlin',
};

const defaultDayOptions: Required<DayFormatOptions> = {
  showDay: true,
  showYear: true,
  useFullMonth: true,
  useFullDay: true,
  noDayString: false,
  useShortMonth: false,
  useShortDay: false,
  useNumericFormat: false,
  locale: 'de-DE',
  timeZone: 'Europe/Berlin',
};

export const formatTimestamp = (timestamp: string, options: TimeFormatOptions = {}): string => {
  const date = new Date(timestamp);
  
  if (isNaN(date.getTime())) {
    return '';
  }

  const opts = { ...defaultOptions, ...options };

  // Use timezone-aware formatting to preserve the original timezone
  const timeZoneOptions = { timeZone: opts.timeZone };
  
  // Get time components using timezone-aware methods
  const timeOptions: Intl.DateTimeFormatOptions = {
    ...timeZoneOptions,
    hour: opts.useNumericTimeFormat ? '2-digit' : 'numeric',
    minute: '2-digit',
    ...(opts.showSeconds && { second: '2-digit' }),
    hour12: !opts.use24Hour
  };

  const timeString = date.toLocaleString(opts.locale, timeOptions);
  
  // Get date components using timezone-aware methods
  const day = date.toLocaleDateString(opts.locale, { 
    ...timeZoneOptions, 
    day: 'numeric' 
  });
  
  const month = opts.useFullMonth 
    ? date.toLocaleDateString(opts.locale, { ...timeZoneOptions, month: 'long' })
    : date.toLocaleDateString(opts.locale, { ...timeZoneOptions, month: 'short' });
    
  const year = date.toLocaleDateString(opts.locale, { 
    ...timeZoneOptions, 
    year: 'numeric' 
  });
  
  const dayName = opts.useFullDay
    ? date.toLocaleDateString(opts.locale, { ...timeZoneOptions, weekday: 'long' })
    : date.toLocaleDateString(opts.locale, { ...timeZoneOptions, weekday: 'short' });

  const parts: string[] = [];

  // Add day name if requested
  if (opts.showDay) {
    parts.push(dayName);
  }

  // Add date if requested
  if (opts.showDate) {
    if (opts.useFullDateTime) {
      parts.push(`${month} ${day}, ${year}`);
    } else {
      parts.push(`${day} ${month}`);
      if (opts.showYear) {
        parts.push(year);
      }
    }
  }

  // Add time if requested
  if (opts.showTime) {
    parts.push(timeString);
  }

  // Special case for full datetime format
  if (opts.useFullDateTime) {
    return `${dayName}, ${month} ${day}, ${year} at ${timeString}`;
  }

  return parts.join(' ');
};

/**
 * Compares two timestamps for equality, normalizing them to the same format first.
 * Handles different timestamp formats (UTC, timezone-aware, etc.)
 * @param timestamp1 - First timestamp to compare
 * @param timestamp2 - Second timestamp to compare
 * @returns true if the timestamps represent the same moment in time
 */
export const areTimestampsEqual = (timestamp1: string | null, timestamp2: string | null): boolean => {
  if (!timestamp1 || !timestamp2) {
    return timestamp1 === timestamp2;
  }
  
  try {
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);
    
    // Check if both dates are valid
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      return false;
    }
    
    // Compare the actual time values (milliseconds since epoch)
    return date1.getTime() === date2.getTime();
  } catch (error) {
    window.clientLogger.warn('Error comparing timestamps:', error);
    return false;
  }
};

export const formatDateStamp = (daystamp: string, options: DayFormatOptions = {}): string => {
  const date = new Date(daystamp);
  
  if (isNaN(date.getTime())) {
    window.clientLogger.error(`Invalid daystamp provided: ${daystamp}`);
    return '';
  }

  const opts = { ...defaultDayOptions, ...options };

  // If numeric format is requested, use Intl.DateTimeFormat directly
  if (opts.useNumericFormat) {
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      timeZone: opts.timeZone
    };
    if (opts.showYear) {
      formatOptions.year = 'numeric';
    }
    return new Intl.DateTimeFormat(opts.locale, formatOptions).format(date);
  }

  // Use timezone-aware formatting
  const timeZoneOptions = { timeZone: opts.timeZone };

  const day = date.toLocaleDateString(opts.locale, { 
    ...timeZoneOptions, 
    day: 'numeric' 
  });
  
  const month = opts.useShortMonth 
    ? date.toLocaleDateString(opts.locale, { ...timeZoneOptions, month: 'short' })
    : date.toLocaleDateString(opts.locale, { ...timeZoneOptions, month: 'long' });
    
  const year = date.toLocaleDateString(opts.locale, { 
    ...timeZoneOptions, 
    year: 'numeric' 
  });
  
  const dayName = opts.useShortDay
    ? date.toLocaleDateString(opts.locale, { ...timeZoneOptions, weekday: 'short' })
    : date.toLocaleDateString(opts.locale, { ...timeZoneOptions, weekday: 'long' });

  const parts: string[] = [];

  // Add day name if requested and not disabled
  if (opts.showDay && !opts.noDayString) {
    parts.push(dayName);
  }

  // Format date based on locale: French uses day first, others use month first
  const isFrench = opts.locale.toLowerCase().startsWith('fr');
  const datePart = isFrench ? `${day} ${month}` : `${month} ${day}`;
  parts.push(datePart);

  // Add year if requested
  if (opts.showYear) {
    parts.push(year);
  }
  return parts.join(', ');
};


export const getMonthsOptionsByLanguage = (languageId: string) => {
  const options: string[] = [];
  const currentYear = new Date().getFullYear();
  const monthList = Array.from({ length: 12 }, (_, i) => i);
  const formatter = new Intl.DateTimeFormat(languageId, { month: 'long' });
  monthList.forEach((monthNumber) => {
    options.push(formatter.format(new Date(currentYear, monthNumber, 1)));
  });
  return options;
};

/**
 * Calculates the time difference in milliseconds between a given timestamp and now.
 * Positive values mean the timestamp is in the future, negative means in the past.
 * @param timestamp - The timestamp to compare against current time (any format that Date can parse)
 * @param from - The reference time to compare against (defaults to current time)
 * @returns Time difference in milliseconds (future = positive, past = negative)
 */
export const getTimeDifference = (timestamp: string | Date | number, from: string | Date | number = Date.now()): number => {
  try {
    const targetDate = new Date(timestamp);
    const fromDate = new Date(from);
    
    if (isNaN(targetDate.getTime()) || isNaN(fromDate.getTime())) {
      return 0;
    }

    return targetDate.getTime() - fromDate.getTime();
  } catch (error) {
    window.clientLogger.warn('Error calculating time difference:', error);
    return 0;
  }
};

// Combines date string (YYYY-MM-DD) with time string (HH:mm:ss) into full datetime, returns timeString as-is if it already contains a date
export const combineDateAndTime = (dateString: string | null, timeString: string): string => {
  if (!dateString) {
    return timeString;
  }
  
  // If timeString already contains a date, return it as-is
  if (timeString.includes('T') || timeString.match(/^\d{4}-\d{2}-\d{2}/)) {
    return timeString;
  }
  
  // Combine date (YYYY-MM-DD) with time (HH:mm:ss) to create full datetime
  return `${dateString}T${timeString}`;
};

// Maps locale_id to timezone
export const getTimezoneFromLocale = (localeId: string): string => {
  const localeToTimezone: { [key: string]: string } = {
    'de-DE': 'Europe/Berlin',
    'de-CH': 'Europe/Zurich',
    'fr-FR': 'Europe/Paris',
    'en-GB': 'Europe/London',
  };
  
  return localeToTimezone[localeId] || 'Europe/Berlin';
};

// Returns formatting options with locale and timezone automatically set from localeId
export const getLocaleFormatOptions = (localeId: string): { locale: string; timeZone: string } => {
  return {
    locale: localeId,
    timeZone: getTimezoneFromLocale(localeId),
  };
};